import { ApiCampaign } from '@highimpact-frontend/api-types';
import { StateComponent } from '@highimpact/dashboard-ui';
import { resolveIntervalToFixed } from '@highimpact/time-intervals';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { forwardRef, LegacyRef, useMemo } from 'react';

import { useCampaignsQuery } from '../../../../query/useCampaignsQuery';
import { resolveElementMeasureColumnNames } from '../../../../runtime/buildElementPlotMetadata';
import { getIntervals } from '../../../../state/dashboard-element-operations';
import { PlotProps } from '../../commons/plots/helpers';
import { DashboardTable } from '../shared/DashboardTable';
import { useStaticElementMeasureRows } from '../shared/useStaticElementMeasureRows';
import { useTableInstanceRegistration } from '../shared/useTableInstanceRegistration';

interface TableByCampaignHelperProps {
  heading: JSX.Element;
  body: JSX.Element[];
}

function TableByCampaignHelper({ heading, body }: TableByCampaignHelperProps) {
  return (
    <DashboardTable>
      {heading}
      <TableBody>{body}</TableBody>
    </DashboardTable>
  );
}

function readElementTimeBounds(elementData: PlotProps['elementData']) {
  const intervals = getIntervals(elementData) ?? [];
  const fixed = intervals[0] ? resolveIntervalToFixed(intervals[0]) : undefined;
  const properties = elementData.properties as {
    start_date?: string;
    end_date?: string;
  };

  return {
    start:
      fixed?.startdate ?? properties.start_date ?? new Date(0).toISOString(),
    end: fixed?.enddate ?? properties.end_date ?? new Date().toISOString(),
  };
}

function TableByCampaign(
  { site, elementData, dashboardElementId, widget_id }: PlotProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const { rows, loading, error, usesElementMeasureRequest, labelMap } =
    useStaticElementMeasureRows({
      element: elementData,
    });

  useTableInstanceRegistration({
    dashboardElementId,
    widgetId: widget_id,
    enabled:
      !(usesElementMeasureRequest && (loading || error)) && site !== undefined,
  });

  const timeBounds = useMemo(
    () => readElementTimeBounds(elementData),
    [elementData]
  );

  const { campaigns: siteCampaigns, loading: campaignsLoading } =
    useCampaignsQuery(site?.id);

  const yColNames = useMemo(() => {
    const axisByIndex = (
      elementData.properties as {
        data_source_to_axis?: Record<number, { axis_type?: string }>;
      }
    ).data_source_to_axis;
    const columnNames = resolveElementMeasureColumnNames(elementData, labelMap);

    return columnNames.filter(
      (_, index) => axisByIndex?.[index]?.axis_type === 'y'
    );
  }, [elementData, labelMap]);

  const campaigns = useMemo(() => {
    const startMs = new Date(timeBounds.start).getTime();
    const endMs = new Date(timeBounds.end).getTime();

    return siteCampaigns.filter((campaign) => {
      const campaignStart = new Date(campaign.start_date).getTime();
      const campaignEnd = new Date(campaign.end_date).getTime();

      return campaignStart < endMs && campaignEnd > startMs;
    }) as ApiCampaign[];
  }, [siteCampaigns, timeBounds.end, timeBounds.start]);

  if (site === undefined) {
    return (
      <div
        ref={ref}
        className="widget-content" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <p>
          Cet élément ne fonctionne que sur les tableaux de bord propres à un
          site
        </p>
      </div>
    );
  }

  if (
    usesElementMeasureRequest &&
    (loading || campaignsLoading || rows === null)
  ) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name={elementData.name ?? 'Tableau'}
          message="Chargement des données..."
          spinner
        />
      </div>
    );
  }

  if (usesElementMeasureRequest && error) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name={elementData.name ?? 'Tableau'}
          message={error.message}
        />
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div
        ref={ref}
        className="widget-content" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <p>Aucune campagne pour cette période !</p>
      </div>
    );
  }

  type RawDatum = Record<string, string | number | boolean | null>;

  const measureRows = (rows ?? []) as RawDatum[];

  const averageMeasuresBydevice = (data: RawDatum[]): RawDatum => {
    const summed = data.reduce((acc, record) => {
      const tups = yColNames
        .map((name) => [name, record[name]] as const)
        .filter(
          (tup): tup is [string, string | number | boolean] =>
            tup[1] !== undefined && tup[1] !== null
        );
      for (const [key, value] of tups) {
        if (acc[key]) {
          (acc[key] as number) += value as number;
        } else {
          acc[key] = value as number;
        }
      }
      return acc;
    }, {} as RawDatum);

    return Object.fromEntries(
      Object.entries(summed).map(([key, value]) => [
        key,
        (value as number) /
          data.filter((entry) => entry[key] !== undefined).length,
      ])
    );
  };

  const agregatedDataByCampaign: RawDatum[] = campaigns.map((campaign) => {
    const campaignData = measureRows.filter(
      (data) =>
        new Date(campaign.start_date) < new Date(data['horodate'] as string) &&
        new Date(data['horodate'] as string) < new Date(campaign.end_date)
    );

    const columnsAllNull: Set<string> = new Set();
    if (campaignData.length > 0) {
      Object.keys(campaignData[0]).forEach((col) => {
        if (col === 'horodate') return;
        const allNull = campaignData.every(
          (row) => row[col] === null || row[col] === undefined
        );
        if (allNull) {
          columnsAllNull.add(col);
        }
      });
    }

    const cleanedData = campaignData.map((row) => {
      const newRow: RawDatum = {};
      Object.entries(row).forEach(([key, value]) => {
        if (!columnsAllNull.has(key)) {
          newRow[key] = value;
        }
      });
      return newRow;
    });

    const agregatedData = averageMeasuresBydevice(cleanedData);

    return {
      ...agregatedData,
      campaign: campaign.name,
    };
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const first = agregatedDataByCampaign[0]!;

  const colNames = Object.keys(first).filter((key) => key !== 'campaign');
  const finalColNames = ['campaign'].concat(colNames);
  const finalColPrettyNames = ['Campagne'].concat(colNames);

  if (colNames.length === 0) {
    return (
      <div
        ref={ref}
        className="widget-content" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <p>Aucune données pour les mesures sélectionnés !</p>
      </div>
    );
  }

  const formatNumber = (value: number | string | boolean): string | boolean =>
    typeof value === 'number' ? value.toFixed(2) : value;

  const heading = (
    <TableHead>
      <TableRow>
        {finalColPrettyNames.map((col) => (
          <TableCell key={col}>{col}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const body = agregatedDataByCampaign.map((row, i) => (
    <TableRow hover key={i}>
      {finalColNames.map((col, j) => (
        <TableCell key={`${i}${j}`}>
          {row[col] !== undefined && row[col] !== null
            ? formatNumber(row[col])
            : '--'}
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <div ref={ref} className="widget-content">
      <TableByCampaignHelper heading={heading} body={body} />
    </div>
  );
}

export default forwardRef(TableByCampaign);
